/*  Proposta B — Hospitality Boutique
    Light, organic, elegante. Inspired by Mews · Apaleo · Cloudbeds.
    Background #F7F5F0 (warm), surface #FFFFFF, primary #2C3E3A (forest), accent #C19A6B (camel).
    Display: Cormorant Garamond (serif) · Body: Outfit (geometric sans).
    Top-nav orizzontale, padding generosi, soft rounded corners.
*/

import React from 'react';
import { Link } from 'react-router-dom';
import { useV2Fonts, MOCK } from './_shared';
import {
  Search, Bell, Plus, TrendingUp, AlertCircle, Sparkles,
  Calendar, ArrowRight, ChevronDown
} from 'lucide-react';

const TOKENS = {
  bg: '#F7F5F0',
  surface: '#FFFFFF',
  primary: '#2C3E3A',
  primarySoft: '#5A6C68',
  accent: '#C19A6B',
  accentSoft: '#E4D3B8',
  border: '#EAE6DF',
  text: '#1A1A1A',
  muted: '#6E6B65',
  success: '#2D7A5D',
  warning: '#D48B3E',
  danger: '#A63C3C',
  fontDisplay: '"Cormorant Garamond", "Playfair Display", serif',
  fontBody: '"Outfit", "Inter", sans-serif',
};

const NAV = [
  { label: 'Panoramica', active: true },
  { label: 'Ticket' },
  { label: 'Programmate' },
  { label: 'Consumi' },
  { label: 'Contratti' },
  { label: 'Analytics' },
];

const PRIORITY_BADGE = {
  urgente: { bg: '#FBE9E9', fg: TOKENS.danger, label: 'Urgente' },
  alta: { bg: '#FBEFD9', fg: TOKENS.warning, label: 'Alta' },
  media: { bg: TOKENS.accentSoft, fg: TOKENS.primary, label: 'Media' },
};

const ProposalB = () => {
  useV2Fonts();

  return (
    <div className="min-h-screen" style={{ background: TOKENS.bg, fontFamily: TOKENS.fontBody, color: TOKENS.text }} data-testid="proposal-b">
      {/* Top nav */}
      <header className="border-b sticky top-0 z-10" style={{ background: TOKENS.surface, borderColor: TOKENS.border }}>
        <div className="max-w-[1400px] mx-auto px-8 h-20 flex items-center gap-10">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: TOKENS.primary }}>
              <Sparkles className="w-4 h-4" style={{ color: TOKENS.accent }} />
            </div>
            <div>
              <div className="text-xl tracking-tight" style={{ fontFamily: TOKENS.fontDisplay, color: TOKENS.primary, fontWeight: 600 }}>
                HostFix
              </div>
              <div className="text-[9px] uppercase tracking-[0.25em]" style={{ color: TOKENS.muted, letterSpacing: '0.3em' }}>
                Invest Hospitality
              </div>
            </div>
          </div>

          <nav className="flex items-center gap-1 flex-1">
            {NAV.map((n) => (
              <button
                key={n.label}
                className="px-4 py-2 text-sm rounded-full transition-colors"
                style={{
                  color: n.active ? TOKENS.surface : TOKENS.primary,
                  background: n.active ? TOKENS.primary : 'transparent',
                  fontWeight: 500,
                }}
              >
                {n.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button className="flex items-center gap-2 px-4 py-2 rounded-full border text-sm" style={{ borderColor: TOKENS.border, color: TOKENS.muted }}>
              <Search className="w-4 h-4" /> Cerca <ChevronDown className="w-3 h-3 opacity-50" />
            </button>
            <button className="p-2.5 rounded-full border" style={{ borderColor: TOKENS.border }}>
              <Bell className="w-4 h-4" style={{ color: TOKENS.primary }} />
            </button>
            <div className="flex items-center gap-2 pl-3 border-l" style={{ borderColor: TOKENS.border }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold" style={{ background: TOKENS.accentSoft, color: TOKENS.primary }}>
                LB
              </div>
              <div className="text-xs leading-tight">
                <div className="font-semibold">{MOCK.user}</div>
                <div style={{ color: TOKENS.muted }}>Hotel Manager</div>
              </div>
            </div>
            <Link to="/mockup/v2" className="text-xs ml-2 hover:underline" style={{ color: TOKENS.muted }}>
              ← Confronta
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-8 py-12">
        {/* Greeting */}
        <div className="mb-10 flex items-end justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: TOKENS.accent }}>
              {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
            <h1 className="text-5xl leading-none" style={{ fontFamily: TOKENS.fontDisplay, color: TOKENS.primary, fontWeight: 600 }}>
              Buongiorno, {MOCK.user.split(' ')[0]}.
            </h1>
            <p className="mt-3 text-base max-w-xl" style={{ color: TOKENS.muted }}>
              <span style={{ color: TOKENS.danger, fontWeight: 600 }}>{MOCK.kpis.critical} ticket critici</span> richiedono la tua attenzione
              questa mattina al <span style={{ color: TOKENS.primary, fontWeight: 500 }}>{MOCK.hotelName}</span>.
            </p>
          </div>
          <button
            className="px-6 py-3 rounded-full flex items-center gap-2 text-sm shadow-sm"
            style={{ background: TOKENS.primary, color: TOKENS.surface }}
          >
            <Plus className="w-4 h-4" /> Nuovo intervento
          </button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-4 gap-5 mb-10">
          {[
            { label: 'Ticket aperti', value: MOCK.kpis.open, sub: '+12% vs ieri', tone: 'warn' },
            { label: 'Tempo medio risoluzione', value: MOCK.kpis.avgResolution, sub: '−18m vs ieri', tone: 'good' },
            { label: 'Conformità SLA', value: MOCK.kpis.sla, sub: '+2pt vs scorsa sett.', tone: 'good' },
            { label: 'Indice efficienza', value: MOCK.consumption.efficiency, sub: 'Top quartile gruppo', tone: 'good' },
          ].map((k) => (
            <div
              key={k.label}
              className="rounded-3xl p-6"
              style={{
                background: TOKENS.surface,
                boxShadow: '0 1px 2px rgba(44,62,58,0.04), 0 8px 24px rgba(44,62,58,0.05)',
              }}
            >
              <div className="text-xs uppercase tracking-wider mb-2" style={{ color: TOKENS.muted }}>
                {k.label}
              </div>
              <div className="text-5xl leading-none tracking-tight" style={{ fontFamily: TOKENS.fontDisplay, color: TOKENS.primary, fontWeight: 600 }}>
                {k.value}
              </div>
              <div className="mt-3 text-xs flex items-center gap-1" style={{ color: k.tone === 'good' ? TOKENS.success : TOKENS.warning }}>
                <TrendingUp className="w-3 h-3" /> {k.sub}
              </div>
            </div>
          ))}
        </div>

        {/* Tickets + Sidebar */}
        <div className="grid grid-cols-3 gap-6 mb-10">
          <div
            className="col-span-2 rounded-3xl p-7"
            style={{ background: TOKENS.surface, boxShadow: '0 1px 2px rgba(44,62,58,0.04), 0 8px 24px rgba(44,62,58,0.05)' }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest mb-1" style={{ color: TOKENS.accent }}>Coda prioritaria</div>
                <h2 className="text-2xl" style={{ fontFamily: TOKENS.fontDisplay, color: TOKENS.primary, fontWeight: 600 }}>
                  Da affrontare ora
                </h2>
              </div>
              <button className="flex items-center gap-1.5 text-sm" style={{ color: TOKENS.primary, fontWeight: 500 }}>
                Tutti i ticket <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              {MOCK.urgent.map((t) => {
                const p = PRIORITY_BADGE[t.priority];
                return (
                  <div
                    key={t.id}
                    className="flex items-center gap-5 p-4 rounded-2xl border hover:border-[#C19A6B] transition-colors cursor-pointer"
                    style={{ borderColor: TOKENS.border }}
                  >
                    <span
                      className="text-xs px-3 py-1 rounded-full font-medium"
                      style={{ background: p.bg, color: p.fg }}
                    >
                      {p.label}
                    </span>
                    <div className="flex-1">
                      <div className="font-medium" style={{ color: TOKENS.text }}>{t.title}</div>
                      <div className="text-xs mt-0.5" style={{ color: TOKENS.muted }}>
                        Camera {t.room} · ticket {t.id}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs" style={{ color: TOKENS.muted }}>aperto da</div>
                      <div className="text-sm font-semibold" style={{ color: TOKENS.primary }}>{t.age}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-6">
            <div
              className="rounded-3xl p-6"
              style={{
                background: `linear-gradient(135deg, ${TOKENS.primary} 0%, #1e2d2a 100%)`,
                color: TOKENS.surface,
              }}
            >
              <Sparkles className="w-5 h-5 mb-3" style={{ color: TOKENS.accent }} />
              <div className="text-xs uppercase tracking-widest opacity-70 mb-2">Energia · 12 mesi</div>
              <div className="text-4xl mb-1" style={{ fontFamily: TOKENS.fontDisplay, fontWeight: 600 }}>
                {MOCK.consumption.efficiency}/100
              </div>
              <div className="text-xs opacity-70 mb-5">Indice efficienza · top quartile</div>

              <div className="h-16 flex items-end gap-1">
                {MOCK.consumption.trend12m.map((v, i) => {
                  const max = Math.max(...MOCK.consumption.trend12m);
                  const h = (v / max) * 100;
                  return (
                    <div
                      key={i}
                      className="flex-1 rounded-t-sm"
                      style={{
                        height: `${h}%`,
                        background: i === 11 ? TOKENS.accent : 'rgba(193,154,107,0.35)',
                        minHeight: 4,
                      }}
                    />
                  );
                })}
              </div>
            </div>

            <div
              className="rounded-3xl p-6"
              style={{ background: TOKENS.surface, boxShadow: '0 1px 2px rgba(44,62,58,0.04), 0 8px 24px rgba(44,62,58,0.05)' }}
            >
              <div className="text-xs uppercase tracking-widest mb-3" style={{ color: TOKENS.accent }}>
                Carico tecnici
              </div>
              {MOCK.technicians.slice(0, 4).map((t) => {
                const pct = Math.min(100, t.open * 14);
                return (
                  <div key={t.name} className="mb-3 last:mb-0">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span style={{ fontWeight: 500 }}>{t.name}</span>
                      <span className="text-xs" style={{ color: TOKENS.muted }}>{t.open}</span>
                    </div>
                    <div className="h-1 rounded-full overflow-hidden" style={{ background: TOKENS.border }}>
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${pct}%`, background: t.open >= 7 ? TOKENS.danger : TOKENS.accent }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Hotel group strip */}
        <div
          className="rounded-3xl p-7"
          style={{ background: TOKENS.surface, boxShadow: '0 1px 2px rgba(44,62,58,0.04), 0 8px 24px rgba(44,62,58,0.05)' }}
        >
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-xs uppercase tracking-widest" style={{ color: TOKENS.accent }}>Gruppo · live</div>
              <h2 className="text-2xl" style={{ fontFamily: TOKENS.fontDisplay, color: TOKENS.primary, fontWeight: 600 }}>
                Tutte le strutture
              </h2>
            </div>
            <Calendar className="w-5 h-5" style={{ color: TOKENS.muted }} />
          </div>
          <div className="grid grid-cols-4 gap-4">
            {MOCK.hotels.map((h) => (
              <div key={h.name} className="rounded-2xl p-5 border" style={{ borderColor: TOKENS.border }}>
                <div className="text-base mb-3" style={{ fontFamily: TOKENS.fontDisplay, color: TOKENS.primary, fontWeight: 600 }}>
                  {h.name}
                </div>
                <div className="flex items-baseline gap-4">
                  <div>
                    <div className="text-xs" style={{ color: TOKENS.muted }}>Ticket</div>
                    <div className="text-2xl font-semibold">{h.open}</div>
                  </div>
                  <div>
                    <div className="text-xs" style={{ color: TOKENS.muted }}>Eff.</div>
                    <div
                      className="text-2xl font-semibold"
                      style={{ color: h.eff >= 65 ? TOKENS.success : TOKENS.warning }}
                    >
                      {h.eff}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProposalB;
